<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <BaseForm
      ref="form"
      v-bind="form"
      novalidate
      class="screen__form"
      @submit="send"
      @show:terms="showTermsModal"
    />
    <ModalTerms
      v-if="isTermsModalVisible"
      slot="root"
      class="screen__overlay"
      @close="hideTermsModal"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import screenMixin from '@/mixins/screen';
import ModalTerms from '@/components/ModalTerms.vue';
import BaseForm from '@/components/BaseForm.vue';
import FieldPhone from '@/components/FieldPhone.vue';
import FieldName from '@/components/FieldName.vue';

import CONSTANTS from '@/helpers/constants';

const {
  SCREEN_READY,
  STEP_AUDIO_FEEDBACK_SUCCESS,
  ACTION_OPEN_MODAL_TERMS,
  ACTION_SEND_AUDIO,
} = CONSTANTS;

export default {
  name: 'SendAudioScreen',
  mixins: [screenMixin],
  components: {
    ModalTerms,
    BaseForm,
  },
  data() {
    return {
      isTermsModalVisible: false,
    };
  },
  computed: {
    ...mapState('feedback', [
      'audio',
    ]),
    title() {
      return this.$t('titles.contacts');
    },
    thanks() {
      return {
        icon: {
          name: 'thanks',
          isBright: true,
        },
        title: this.$t('titles.thanks'),
        text: this.$t('texts.thanksForFeedback'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    error() {
      return {
        title: this.$t('titles.badHardware'),
        text: this.$t('texts.feedbackNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    form() {
      return {
        fields: [
          {
            id: 'name',
            component: FieldName,
          },
          {
            id: 'phone',
            component: FieldPhone,
            defaultValue: '+7',
          },
        ],
        hasTermsLink: true,
        submitButtonText: this.$t('actions.sendFeedback'),
      };
    },
  },
  mounted() {
    this.initActions();
  },
  methods: {
    ...mapActions('feedback', [
      'sendAudio',
    ]),
    async send(data) {
      if (this.isPending) return;
      try {
        this.showPending();
        await this.sendAudio(data);
        this.showThanks();
      } catch {
        this.sendMail(data);
      }
    },
    sendMail({ name, phone }) {
      const subject = this.$t('titles.theatreFeedbackWasLeaved');
      const email = process.env.VUE_APP_FEEDBACK_EMAIL;
      const file = this.audio?.fullPath;

      const body = [
        [this.$t('fields.fullname'), name],
        [this.$t('fields.phoneNumber'), phone],
      ]
        .map((el) => el.join(': '))
        .join('\n');

      return this.$robot.mailService.sendMail(email, subject, body, file);
    },
    showTermsModal() {
      this.isTermsModalVisible = true;
    },
    hideTermsModal() {
      this.isTermsModalVisible = false;
    },
    sayThanks() {
      this.$robot.dialogService.setCase(STEP_AUDIO_FEEDBACK_SUCCESS, true, true);
      this.$robot.dialogService.sayReplicByName(STEP_AUDIO_FEEDBACK_SUCCESS, true);
    },
    setFieldFocus() {
      return this.$refs.form?.setFocus();
    },
    submitForm() {
      return this.$refs.form?.submit();
    },
    initActions() {
      const robotActions = [
        this.$robot.mailService.onSuccess(this.showThanks),
        this.$robot.mailService.onError(this.showError),
      ];
      const rootActions = [
        [SCREEN_READY, this.setFieldFocus],
        [ACTION_SEND_AUDIO, this.submitForm],
        [ACTION_OPEN_MODAL_TERMS, this.showTermsModal],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
        robotActions.forEach((unsubscribe) => unsubscribe());
      });
    },
  },
};
</script>
